/**
 * Accept.js – get payment nonce from Authorize.net (card data replaced by token).
 * - Staging & production builds: always https://js.authorize.net/v1/Accept.js (merchant + Basil).
 * - Local Vite dev only (npm run start): first call can use sandbox (jstest) if you use sandbox test keys.
 * - Second call (Basil) still uses prod URL when dual tokenization runs.
 *
 * Credentials come from /onlineOrderSettings/menu API; OnlineOrderSettingsContext
 * stores apiLogInId and publicClientKey in sessionStorage (acceptJsApiLoginID, acceptJsPublicClientKey).
 */

const SESSION_KEY_API_LOGIN_ID = "acceptJsApiLoginID";
const SESSION_KEY_PUBLIC_CLIENT_KEY = "acceptJsPublicClientKey";
const SESSION_KEY_BASIL_API_LOGIN_ID = "acceptJsBasilApiLoginID";
const SESSION_KEY_BASIL_PUBLIC_CLIENT_KEY = "acceptJsBasilPublicClientKey";

/** Sandbox URL – used for first (merchant) tokenization in dev only */
const ACCEPT_JS_SANDBOX_URL = "https://jstest.authorize.net/v1/Accept.js";

/** Prod URL – used for second (Basil) call in dev and prod, and for both calls in prod */
export const ACCEPT_JS_PROD_URL = "https://js.authorize.net/v1/Accept.js";

/** Currently loaded script URL (so we can switch to prod for second call if needed) */
let loadedAcceptJsUrl: string | null = null;

const ACCEPT_CORE_READY_MS = 15_000;

/**
 * Accept.js main script sets window.Accept immediately, but AcceptCore.js loads async.
 * Tokenizing before the "handshake" yields errors or "not loaded". Wait until core is ready.
 */
function waitForAcceptJsCoreReady(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (typeof window === "undefined" || !window.Accept) {
      reject(new Error("Accept.js is not loaded."));
      return;
    }
    if (window.isReady === true) {
      resolve();
      return;
    }

    const body = document.body;
    if (!body) {
      reject(new Error("Accept.js is not loaded."));
      return;
    }

    let settled = false;
    let poll: ReturnType<typeof setInterval>;
    let t: ReturnType<typeof setTimeout>;

    const cleanup = () => {
      clearInterval(poll);
      clearTimeout(t);
      body.removeEventListener("handshake", onHandshake);
      document.removeEventListener("handshake", onHandshake, true);
    };

    const tryFinish = () => {
      if (settled) return;
      if (window.isReady === true && window.Accept) {
        settled = true;
        cleanup();
        resolve();
      }
    };

    const onHandshake = () => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve();
    };

    body.addEventListener("handshake", onHandshake);
    document.addEventListener("handshake", onHandshake, true);
    poll = setInterval(tryFinish, 50);
    t = setTimeout(() => {
      if (settled) return;
      settled = true;
      cleanup();
      if (window.Accept && window.isReady === true) {
        resolve();
      } else if (window.Accept) {
        console.warn(
          "[Accept.js] Core readiness timeout; proceeding if Accept.dispatchData exists (Authorize.net may not set isReady on this build)."
        );
        resolve();
      } else {
        reject(
          new Error(
            "Accept.js core did not finish loading. Please try again."
          )
        );
      }
    }, ACCEPT_CORE_READY_MS);
  });
}

/**
 * Sandbox (jstest) only while running the Vite dev server locally.
 * Staging and production hosts use prod Accept.js — same as production Auth.net.
 */
/** Vite injects via `define` in vite.config.ts; Jest reads real process.env. */
function envStr(key: string): string | undefined {
  const v =
    typeof process !== "undefined" && process.env?.[key]
      ? String(process.env[key]).trim()
      : "";
  return v || undefined;
}

function shouldUseSandboxForMerchant(): boolean {
  if (typeof process !== "undefined" && process.env) {
    if (process.env.NODE_ENV === "development") return true;
    if (String(process.env.VITE_ACCEPT_JS_MERCHANT_SANDBOX || "").toLowerCase() === "true")
      return true;
    if (String(process.env.VITE_APP_ENV || "").toLowerCase() === "development") return true;
  }
  if (typeof window !== "undefined" && window.location?.hostname) {
    const h = window.location.hostname.toLowerCase();
    if (h === "localhost" || h === "127.0.0.1") return true;
    if (h.includes("dev")) return true;
  }
  return false;
}
/**
 * Ensure Accept.js is loaded.
 * - No options: prod URL on staging/production; sandbox when NODE_ENV=development, VITE_ACCEPT_JS_MERCHANT_SANDBOX, VITE_APP_ENV, or localhost/dev hostname.
 * - useProdUrl: true = always load prod (second call / Basil). Use before second tokenization when dual tokenization runs.
 */
export function loadAcceptJs(options?: { useProdUrl?: boolean }): Promise<void> {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("Not in browser"));
  }
  const useProd = options?.useProdUrl === true;
  const sandboxForMerchant = shouldUseSandboxForMerchant();
  const desiredUrl = useProd
    ? ACCEPT_JS_PROD_URL
    : sandboxForMerchant
      ? ACCEPT_JS_SANDBOX_URL
      : ACCEPT_JS_PROD_URL;
  if (window.Accept && loadedAcceptJsUrl === desiredUrl) {
    return waitForAcceptJsCoreReady();
  }
  const existingByUrl = document.querySelector(`script[src="${desiredUrl}"]`);
  if (existingByUrl && window.Accept) {
    loadedAcceptJsUrl = desiredUrl;
    return waitForAcceptJsCoreReady();
  }

  return new Promise((resolve, reject) => {
    const timeout = 15_000;
    const deadline = Date.now() + timeout;
    /** When switching sandbox → prod (merchant then Basil), stale isReady/Accept caused E_WC_03 "Accept.js is not loaded correctly". */
    const removeAuthorizeNetScripts = () => {
      try {
        delete (window as unknown as { isReady?: boolean }).isReady;
        (window as unknown as { Accept?: unknown }).Accept = undefined;
      } catch {
        /* ignore */
      }
      document.querySelectorAll('script[src*="authorize.net"]').forEach((el) => el.remove());
      loadedAcceptJsUrl = null;
    };
    removeAuthorizeNetScripts();
    const script = document.createElement("script");
    script.type = "text/javascript";
    script.charset = "utf-8";
    script.src = desiredUrl;
    script.onload = () => {
      const interval = setInterval(() => {
        if (window.Accept) {
          clearInterval(interval);
          loadedAcceptJsUrl = desiredUrl;
          waitForAcceptJsCoreReady()
            .then(() => {
              resolve();
            })
            .catch(reject);
        } else if (Date.now() > deadline) {
          clearInterval(interval);
          reject(new Error(`Accept.js loaded but window.Accept not set. Check CSP allows ${desiredUrl}.`));
        }
      }, 50);
    };
    script.onerror = () =>
      reject(
        new Error(
          `Accept.js failed to load. Allow ${desiredUrl} in Content-Security-Policy script-src.`
        )
      );
    document.head.appendChild(script);
  });
}

/** Read Accept.js credentials from sessionStorage (set by menu API response in OnlineOrderSettingsContext) */
function getCredentials(): { apiLoginID: string; publicClientKey: string } {
  if (typeof sessionStorage === "undefined") {
    return { apiLoginID: "", publicClientKey: "" };
  }
  return {
    apiLoginID: sessionStorage.getItem(SESSION_KEY_API_LOGIN_ID)?.trim() ?? "",
    publicClientKey: sessionStorage.getItem(SESSION_KEY_PUBLIC_CLIENT_KEY)?.trim() ?? "",
  };
}

/** Basil credentials: from sessionStorage (menu API – basilPublicClientKey; optional basilApiLogInId). Uses merchant apiLoginID when basil one is not set so delivery tokenization works with only basilPublicClientKey from API. */
export function getBasilCredentials(): { apiLoginID: string; publicClientKey: string } {
  if (typeof sessionStorage === "undefined") {
    return { apiLoginID: "", publicClientKey: "" };
  }
  let apiLoginID =
    sessionStorage.getItem(SESSION_KEY_BASIL_API_LOGIN_ID)?.trim() ??
    envStr("VITE_BASIL_ACCEPT_API_LOGIN_ID") ??
    "";
  const publicClientKey = sessionStorage.getItem(SESSION_KEY_BASIL_PUBLIC_CLIENT_KEY)?.trim() ?? "";
  if (!apiLoginID && publicClientKey) {
    apiLoginID = sessionStorage.getItem(SESSION_KEY_API_LOGIN_ID)?.trim() ?? "";
  }
  return { apiLoginID, publicClientKey };
}

/** Result from Accept.js: send dataDescriptor + dataValue to your backend instead of raw card */
export interface AcceptJsOpaqueData {
  dataDescriptor: string;
  dataValue: string;
}

export type AcceptJsCredentials = { apiLoginID: string; publicClientKey: string };

function dispatchAcceptJs(
  cardNumber: string,
  expMonth: string,
  expYear: string,
  cardCode: string,
  credentials: AcceptJsCredentials,
  label: string
): Promise<AcceptJsOpaqueData> {
  const hint =
    typeof window !== "undefined" && window.location?.protocol === "https:"
      ? " Script may be blocked (e.g. Content Security Policy). Allow https://jstest.authorize.net."
      : " Page must be served over HTTPS.";

  return Promise.resolve()
    .then(() => {
      if (typeof window === "undefined" || !window.Accept) {
        throw new Error("Accept.js is not loaded." + hint);
      }
      return waitForAcceptJsCoreReady();
    })
    .then(() => {
      if (typeof window === "undefined" || !window.Accept) {
        throw new Error("Accept.js is not loaded." + hint);
      }

      const { apiLoginID, publicClientKey } = credentials;
      if (!apiLoginID || !publicClientKey) {
        throw new Error(
          label === "basil"
            ? "Basil payment is not configured. Set acceptJsBasilApiLoginID (e.g. from menu API) or VITE_BASIL_ACCEPT_API_LOGIN_ID."
            : "Payment is not configured. Menu API (onlineOrderSettings/menu) must return apiLogInId and publicClientKey."
        );
      }

      const authData: { clientKey: string; apiLoginID: string } = {
        clientKey: publicClientKey,
        apiLoginID,
      };
      const cardData = {
        cardNumber: cardNumber.replace(/\s/g, ""),
        month: expMonth,
        year: expYear,
        cardCode,
      };
      const secureData = { authData, cardData };

      return new Promise<AcceptJsOpaqueData>((resolve, reject) => {
        window.Accept!.dispatchData(secureData, (response: {
          messages: { resultCode: string; message: Array<{ text: string }> };
          opaqueData?: { dataDescriptor: string; dataValue: string };
        }) => {
          if (response.messages.resultCode === "Error") {
            const msg = response.messages.message?.map((m) => m.text).join(" ") || "Payment failed.";
            reject(new Error(msg));
            return;
          }
          if (!response.opaqueData?.dataValue) {
            reject(new Error("Payment tokenization failed. Please try again."));
            return;
          }
          const dataDescriptor = response.opaqueData.dataDescriptor || "COMMON.ACCEPT.INAPP.PAYMENT";
          const dataValue = response.opaqueData.dataValue;
          resolve({ dataDescriptor, dataValue });
        });
      });
    });
}

/**
 * Tokenize with merchant credentials (from sessionStorage / menu API).
 * Use for order amount.
 */
export function tokenizeCardWithAcceptJs(
  cardNumber: string,
  expMonth: string,
  expYear: string,
  cardCode: string
): Promise<AcceptJsOpaqueData> {
  return dispatchAcceptJs(
    cardNumber,
    expMonth,
    expYear,
    cardCode,
    getCredentials(),
    "merchant"
  );
}

/**
 * Tokenize with explicit credentials (e.g. Basil for delivery fees/tips).
 * Use for delivery: call once with merchant key (order), once with basil key (fees/tips).
 */
export function tokenizeCardWithAcceptJsWithCredentials(
  cardNumber: string,
  expMonth: string,
  expYear: string,
  cardCode: string,
  credentials: AcceptJsCredentials
): Promise<AcceptJsOpaqueData> {
  return dispatchAcceptJs(
    cardNumber,
    expMonth,
    expYear,
    cardCode,
    credentials,
    "basil"
  );
}

/** Parse "MM/YY" or "MM/YYYY" into { month, year } for Accept.js cardData */
export function parseExpiration(expirationDate: string): { month: string; year: string } {
  const parts = String(expirationDate || "").split("/").map((p) => p.trim());
  let month = parts[0] || "";
  let year = parts[1] || "";
  if (year.length === 4) year = year.slice(-2);
  return { month, year };
}
